"use client";

import { Box, type BoxProps } from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";

interface MagneticProps extends BoxProps {
  strength?: number;
  children: React.ReactNode;
}

/**
 * Wraps an element so it nudges toward the cursor on hover, then springs
 * back on leave — plain mousemove + CSS transition, no animation library.
 * Meant for small, isolated interactive elements (icon buttons, pills);
 * overusing it on body text or large regions reads as gimmicky.
 */
export const Magnetic = ({
  strength = 0.35,
  children,
  ...boxProps
}: MagneticProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [springBack, setSpringBack] = useState(false);

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = ref.current;
      if (el == null) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * strength;
      const y = (e.clientY - rect.top - rect.height / 2) * strength;
      setSpringBack(false);
      setOffset({ x, y });
    },
    [strength],
  );

  const onMouseLeave = useCallback(() => {
    setSpringBack(true);
    setOffset({ x: 0, y: 0 });
  }, []);

  return (
    <Box
      ref={ref}
      display="inline-block"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      transform={`translate(${offset.x}px, ${offset.y}px)`}
      transitionProperty="transform"
      transitionDuration={springBack ? "0.5s" : "0.1s"}
      transitionTimingFunction={
        springBack ? "cubic-bezier(0.34, 1.56, 0.64, 1)" : "ease-out"
      }
      {...boxProps}
    >
      {children}
    </Box>
  );
};
